import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { HttpApiResponseInterface } from 'pf-frontend-common/src/module/http/api-response.interface';
import { JsonApiDataInterface } from 'pf-frontend-common/src/module/json-api/data.interface';
import { JsonApiPayloadInterface } from 'pf-frontend-common/src/module/json-api/payload.interface';
import { JsonApiStore } from 'pf-frontend-common/src/module/json-api/store';
import { ApiServiceInterface } from 'pf-frontend-common/src/service/api/service.interface';

import { BuildingReviewModel } from 'common/data/building-review/model';
import { BuildingReviewResponseInterface } from 'common/data/building-review/response.interface';
import { objectFilterFalsy } from 'common/helper/object/filter/falsy';
import { objectFilterNonOrEmptyValue } from 'common/helper/object/filter/non-or-empty-value';
import { DataStorePaginationAdapter } from 'common/module/data/store/pagination-adapter';
import { JsonApiMetaPaginationInterface } from 'common/module/json-api/meta/pagination.interface';
import { ApiEndpointServiceInterface } from 'common/service/api-endpoint/service.interface';
import { BuildingReviewStoreServiceInterface } from 'common/service/building-review-store/service.interface';

export class BuildingReviewStore implements BuildingReviewStoreServiceInterface {
  /**
   * API endpoint
   */
  protected endpoint: { [key: string]: DataKeyValueStringInterface } = {
    create: {
      path: this.apiEndpointService.getPath('/location/{locationId}/building-reviews'),
      method: 'POST',
    },
    getReviews: {
      path: this.apiEndpointService.getPath('/location/{locationId}/building-reviews'),
      method: 'GET',
    },
  };

  /**
   * Constructor
   */
  constructor(
    protected apiService: ApiServiceInterface,
    protected apiEndpointService: ApiEndpointServiceInterface,
    private jsonApiStore: JsonApiStore<BuildingReviewModel>
  ) {}

  /**
   * @inheritDoc
   */
  public create(locationId: string, buildingReviewModel: BuildingReviewModel): Promise<HttpApiResponseInterface> {
    return new Promise((resolve, reject) => {
      // Prepare path
      const path = this.endpoint.create.path.replace('{locationId}', String(locationId));

      // Prepare payload
      const payload: JsonApiPayloadInterface = buildingReviewModel.serialize();
      // Remove unnecessary payload fields
      const payloadData = payload.data as JsonApiDataInterface;
      payloadData.meta = null;
      payloadData.attributes = objectFilterFalsy(payloadData.attributes);
      payload.data = objectFilterNonOrEmptyValue(payloadData);

      // Send request
      this.apiService
        .request(this.endpoint.create.method, path, objectFilterNonOrEmptyValue(payload), true)
        .then((response: HttpApiResponseInterface) => {
          // Failure response
          if (response.status >= 300) {
            reject(response);

            return;
          }

          // Success response
          resolve(response);
        })
        .catch((response: HttpApiResponseInterface) => {
          // Failure response
          reject(response);
        });
    });
  }

  /**
   * @inheritDoc
   */
  public getReviews(locationId: string, pageNumber: number): Promise<BuildingReviewResponseInterface> {
    return new Promise((resolve, reject) => {
      // Prepare path
      const path = this.endpoint.getReviews.path.replace('{locationId}', String(locationId));

      // Send request
      this.apiService
        .request(this.endpoint.getReviews.method, path, {
          page: pageNumber,
        })
        .then((response: HttpApiResponseInterface) => {
          // Failure response
          if (response.status >= 300) {
            reject(response);

            return;
          }

          // Get specialized output
          const output = this.getSearchOutput(<JsonApiPayloadInterface>response.data);

          // Success response
          resolve(output);
        })
        .catch((response: HttpApiResponseInterface) => {
          // Failure response
          reject(response);
        });
    });
  }

  /**
   * @inheritDoc
   */
  public getSearchOutput(payload: JsonApiPayloadInterface): BuildingReviewResponseInterface {
    this.jsonApiStore.reset();
    // Get JSONAPI data & meta, while warming up the store
    const sync = this.jsonApiStore.syncWithMeta(payload);

    return {
      buildingReviews: Array.isArray(sync.data) ? <BuildingReviewModel[]>sync.data : [],
      pagination: new DataStorePaginationAdapter(<JsonApiMetaPaginationInterface>sync.meta).getData(),
    };
  }
}
